import { useCallback, useRef, useState } from 'react';
import { Tooltip } from '@aimsos/ui';

/**
 * Single-line label that reveals its full value on hover, but only once the
 * ellipsis actually kicks in — the rail is fixed-width while titles, statuses
 * and due labels are not, so truncation is measured rather than guessed.
 *
 * The Tooltip stays mounted and toggles `disabled` instead of being swapped in
 * and out: remounting the trigger would remeasure a freshly laid out node and
 * can oscillate between the two states.
 */
function TruncatedText({ text, className = '', triggerClassName = 'flex w-full min-w-0 cursor-default' }) {
  const [truncated, setTruncated] = useState(false);
  const observerRef = useRef(null);

  const attach = useCallback(
    (node) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;

      const measure = () => setTruncated(node.scrollWidth > node.clientWidth + 1);
      measure();
      if (typeof ResizeObserver === 'undefined') return;
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(node);
    },
    // Re-attaching on text changes remeasures a node whose width never changed,
    // which a ResizeObserver alone would miss.
    [text],
  );

  return (
    <Tooltip
      content={text}
      size="compact"
      position="top"
      align="start"
      arrow={false}
      disabled={!truncated}
      triggerClassName={triggerClassName}
    >
      <span ref={attach} className={['block w-full truncate', className].filter(Boolean).join(' ')}>
        {text}
      </span>
    </Tooltip>
  );
}

export default TruncatedText;
